import React, { lazy, Suspense, useEffect, useRef } from "react";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";

import type Page from "../models/Page";
import type Button from "../models/Button";
import { ConnectionType, dehighlightConnections, highlightConnections } from "../models/Connection";
import { ChannelType } from "../models/ChannelType";

import Logger from "../Logger";
import ButtonChannelView from "./ButtonChannelView";
import { itx } from "../translationItx/itx";
import BaseDialog from "../modelDialogs/BaseDialog";
import ButtonChannel from "../models/ButtonChannel";
import Channel from "../models/Channel";
const TemplateSelection = lazy(() => import("../components/TemplateSelection").then(module => ({ default: module.TemplateSelection })));

import { ApplicationMode } from "../models/SettingsEnums";

const logger = new Logger();

function handleMouseUpInHeader(button: Button) {
  const connection = state.currentDrawingConnection.connection;
  if (connection && connection.fromDevice) {
    const toConnectChannel = button.findConnectableChannel(connection.type, connection.fromDevice);
    if (toConnectChannel && state.currentDrawingConnection.page) {
      let connectionType = ConnectionType.Channel;
      const partner = state.currentDrawingConnection.connection?.getPartner(toConnectChannel);
      if (partner?.className == "Scene") {
        connectionType = ConnectionType.Scene;
      }
      if (toConnectChannel.isNewCreatedFromAutoconnect) {
      }
      state.currentDrawingConnection.connect(toConnectChannel, connectionType);

      button.raiseOnChange();
    }
  }
}

export default function ButtonChannelsView(props: { page: Page }) {
  const { page } = props;

  const lastButton = useRef<Button | undefined>(undefined);

  useOnChangeWatcher(state.showButtonChannels, (changedProperty, changedObject) => {
    const button = state.showButtonChannels.value?.button;
    if (button) {
      button.collapseMode = 2;
    } else {
      if (lastButton.current) {
        lastButton.current.collapseMode = 0;
      }
    }
    page.updateConnections();

    return true;
  });

  useOnChangeWatcher(state.settings);

  if (!state.showButtonChannels.value) {
    return null;
  }

  const button = state.showButtonChannels.value.button;
  lastButton.current = button;

  return <ButtonChannelsViewInner page={page} button={button} />;
}

function ButtonChannelsViewInner(props: { page: Page; button: Button }) {
  const { page, button } = props;

  useEffect(() => {
    highlightConnections(true, page, button);
  }, [page, button]);

  useOnChangeWatcher(button, (changedProperty, changedObject) => {
    if (button.isDeleted) {
      state.showButtonChannels.value = undefined; // automatically close dialog if button is deleted
    }

    page.updateConnections();
    return true;
  });

  const scrollerRef = useRef<HTMLDivElement>(null);
  const mouseIsDown = useRef<boolean>(false);

  // logger.log(`render consumer ${button.id} ${position.x} ${position.y}`);

  const borderSize = 5;

  button.center.x = 0;
  // consumer.center.x = state.settings.consumerIconSizeInPX() / 2 + borderSize;
  button.center.y = 1 / 2 + borderSize;

  const channelElements: any = [];

  var channels = button.channelsSortedForDisplay({
    actorChannels: false,
    logicChannels: false,
    environmentChannel: false,
    temperatureMeasurementChannel: false,
    channelWithoutKnxChannel: true,
    generalChannel: false,
    specialChannel: false,
    manualChannel: false,
    unknownChannel: false,
    ignoreChannel: false,
  });

  channels.forEach((channel) => {
    if (
      channel.channelType != ChannelType.General &&
      channel.channelType != ChannelType.Unknown &&
      !channel.isActorChannel
    ) {
      channelElements.push(
        <tr key={channel.id}>
          <td style={{ padding: "0px", margin: "0px" }}>
            <ButtonChannelView mainPage={page} page={page} button={button} channel={channel} />
          </td>
        </tr>,
      );
    }
  });

  if (button.template == undefined) {
    let newChannel: Channel | undefined;

    newChannel = new ButtonChannel({ tag: "@NEW", name: "Neue Taste", channelType: ChannelType.Generic }, button);

    if (newChannel) {
      channelElements.push(
        <tr key="addChannel">
          <td style={{ padding: "0px", margin: "0px" }}>
            <ButtonChannelView mainPage={page} page={page} button={button} channel={newChannel} />
          </td>
        </tr>,
      );
    }
  }
  const actorChannels = button.channels
    .sortBy("tag")
    .filter((c) => c.isActorChannel)
    .filter((c) => c.channelType != ChannelType.General && c.channelType != ChannelType.Unknown);
  if (button.template == undefined && (button.isDecentralActor || actorChannels.length > 0)) {
    channelElements.push(
      <div
        key="decentralActorInfo"
        style={{ padding: "10px", fontStyle: "italic", color: "var(--textColorSecondary)", height: "10px" }}
      ></div>,
    );
  }
  // now ActorChannels here
  actorChannels.forEach((channel) => {
    if (channel.channelType != ChannelType.General && channel.channelType != ChannelType.Unknown) {
      channelElements.push(
        <tr key={channel.id}>
          <td style={{ padding: "0px", margin: "0px" }}>
            <ButtonChannelView mainPage={page} page={page} button={button} channel={channel} />
          </td>
        </tr>,
      );
    }
  });
  if (button.template == undefined && button.isDecentralActor) {
    let newChannelActor: Channel | undefined;

    newChannelActor = new Channel({ tag: "@NEWC", name: "Neuer Kanal", channelType: ChannelType.Generic }, button);

    if (newChannelActor) {
      channelElements.push(
        <tr key="addChannelActor">
          <td style={{ padding: "0px", margin: "0px" }}>
            <ButtonChannelView mainPage={page} page={page} button={button} channel={newChannelActor} />
          </td>
        </tr>,
      );
    }
  }

  function clickClose() {
    try {
      button.collapseMode = 0;

      setTimeout(() => {
        button.updateConnectionEndpoints();
      }, 100);

      state.showButtonChannels.value = undefined;
    } catch (ex) {}
  }

  let header = button.isButton
    ? ` ${state.translate("components.plural.ButtonChannel", "Tasten")}/${state.translate("components.plural.Channel", "Kanäle")}`
    : itx("components.plural.SensorNew", "Sensoren");

  header = `${button.tag} ${header}`;

  return (
    <BaseDialog
      className="ButtonChannelsView connectionTarget ignoreHover"
      isModal={false}
      noPaddingForContent={true}
      noOkButton={true}
      noAbortButton={true}
      noMinHeight={true}
      noMinWidth={true}
      noScroll={true}
      style={{
        backdropFilter: "blur(5px) brightness(0.85)",
        borderRadius: "20px",
      }}
      backgroundColor="transparent"
      onClick={() => {
        state.selectionManager.selectedObject = button;
      }}
      onDoubleClick={() => {
        button.treeViewModel.handleOpen();
      }}
      onMouseMove={(ev) => {
        ev.stopPropagation();
        highlightConnections(true, page, button);
      }}
      onMouseLeave={(ev) => {
        ev.stopPropagation();
        dehighlightConnections(true, page, button);
      }}
      onMouseUp={(ev: any) => {
        if (!mouseIsDown.current) {
          //
          // got "drop" from consumer connection grab
          //
          handleMouseUpInHeader(button);
        }
        mouseIsDown.current = false;
      }}
      onMouseDown={(ev: any) => {
        mouseIsDown.current = true;
        state.currentEditObject.value = undefined;
      }}
      title={header}
      onClose={() => {
        clickClose();
        return true;
      }}
      onMove={() => {
        page.recalcConnections.value++;
      }}
    >
      <div
        ref={scrollerRef}
        className="scrollcontainer"
        style={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          pointerEvents: "none",
          maxHeight: "70vh",
          padding: "20px",
          paddingTop: "22px", // keep unlink button visible
          paddingBottom: "22px", // keep unlink button visible
          backgroundColor: "transparent",
        }}
        onScroll={(ev) => {
          page.recalcConnections.value++;
        }}
      >
        <div
          style={{ pointerEvents: "auto" }}
          onClick={event => event.stopPropagation()}
          onDoubleClick={event => event.stopPropagation()}
          onMouseDown={event => event.stopPropagation()}
          onMouseUp={event => event.stopPropagation()}
        >
          <Suspense fallback={null}><TemplateSelection device={button}
              productType={button.isSensor ? "Sensor" : button.isDecentralActor ? "DecentralActor" : button.isButton ? "Button" : "Actor"}
              context={state.settings.applicationMode === ApplicationMode.ConceptOne ? "c1" : "main"}
              compact
            /></Suspense>
        </div>
        <table
          style={{
            width: "100%",
            height: "100%",
            margin: "0px",
            padding: "10px",
          }}
        >
          {channelElements}
        </table>
      </div>
    </BaseDialog>
  );
}
